/**
 * mail_sendMessage Command
 * 
 * Send an email message
 * Creates Mail.Message entity and sends via provider
 */

import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../../types/commands';
import useEntityStore from '../../../../stores/entityStore';
import type { EmailParticipant } from '../../types/entities';
import { gmailAdapter } from '../../services/gmailAdapter';
import { parseEmailList, isValidEmail } from '../../utils/emailHelpers';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';

export const mailSendMessageCommand: SimpleDocCommand = {
  id: 'mail_sendMessage',
  logicalName: 'mail_sendMessage',
  version: 'v1',
  
  description: `Send an email message via connected integration.

**SUPPORTED INTEGRATIONS:**
Gmail (more providers coming)

**EXAMPLES:**

Send new email:
{
  "integrationId": "integration_123",
  "to": "john@example.com",
  "subject": "Meeting Tomorrow",
  "body": "Hi John,\\n\\nLet's meet at 2pm tomorrow.\\n\\nBest,\\nMe"
}

Reply to message:
{
  "integrationId": "integration_123",
  "to": "john@example.com",
  "subject": "Re: Project Update",
  "body": "Thanks for the update!",
  "inReplyTo": "mail_message_xyz",
  "threadId": "mail_thread_abc"
}

**IMPORTANT:**
User will be asked to confirm before sending.`,
  
  schema: {
    parameters: [
      {
        name: 'integrationId',
        type: 'string',
        description: 'Mail integration ID to send from',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'integrationId is required';
          }
          return null;
        }
      },
      {
        name: 'to',
        type: 'string',
        description: 'Recipients (comma-separated emails or "Name <email>" format)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'to is required';
          }
          return null;
        }
      },
      {
        name: 'subject',
        type: 'string',
        description: 'Email subject',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'subject is required';
          }
          return null;
        }
      },
      {
        name: 'body',
        type: 'string',
        description: 'Email body (plain text)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'body is required';
          }
          if (value.length > 100000) {
            return 'Email body must be less than 100,000 characters';
          }
          return null;
        }
      },
      {
        name: 'cc',
        type: 'string',
        description: 'CC recipients (comma-separated)',
        required: false,
      },
      {
        name: 'bcc',
        type: 'string',
        description: 'BCC recipients (comma-separated)',
        required: false,
      },
      {
        name: 'inReplyTo',
        type: 'string',
        description: 'Message ID if replying',
        required: false,
      },
      {
        name: 'threadId',
        type: 'string',
        description: 'Thread ID if part of conversation',
        required: false,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          integrationId: 'integration_123',
          to: 'john@example.com',
          subject: 'Meeting Tomorrow',
          body: 'Hi John,\n\nLet\'s meet at 2pm tomorrow.\n\nBest,\nMe',
        },
        expectedOutput: createSuccessResult(
          'Email sent to john@example.com',
          { 
            messageId: 'msg_abc',
            to: ['john@example.com'],
            subject: 'Meeting Tomorrow'
          }
        ),
        description: 'Send simple email',
      },
      {
        input: {
          integrationId: 'integration_123',
          to: 'john@example.com',
          subject: 'Re: Project Update',
          body: 'Thanks for the update!',
          inReplyTo: 'mail_message_xyz'
        },
        expectedOutput: createSuccessResult(
          'Email sent to john@example.com',
          { 
            messageId: 'msg_def',
            to: ['john@example.com'],
            subject: 'Re: Project Update'
          }
        ),
        description: 'Reply to existing message',
      },
    ],
    bad: [
      {
        input: {
          integrationId: 'integration_123',
          subject: 'Test'
        },
        error: 'to is required',
        why: 'Missing required recipient',
        fix: 'Provide to parameter with email address'
      },
      {
        input: {
          integrationId: 'integration_123',
          to: 'invalid-email',
          subject: 'Test',
          body: 'Test'
        },
        error: 'No valid email addresses in "to" field',
        why: 'Invalid email format',
        fix: 'Provide valid email addresses'
      }
    ],
  },
  
  formatViewData: (result: any, toolInput: any) => {
    if (result.status === 'success') {
      return {
        type: 'mail_send',
        summary: result.message,
        data: result.data
      };
    }
    return null;
  },
  
  handler: async (
    params: {
      integrationId: string;
      to: string;
      subject: string;
      body: string;
      cc?: string;
      bcc?: string;
      inReplyTo?: string;
      threadId?: string;
    },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const { integrationId, to, subject, body, cc, bcc, inReplyTo, threadId } = params;
      
      console.log('[MailApp:SendMessage] Sending email', { to, subject, hasBody: !!body });
      
      // Validate integration exists
      const entityState = useEntityStore.getState();
      const integration = entityState.entities.find(
        (e: any) => e._id === integrationId && (e.entityType === 'Integration' || e.entityType?.startsWith?.('Integration.'))
      );
      
      if (!integration) {
        return createErrorResult(
          'Integration not found',
          `No mail integration found with ID: ${integrationId}`,
          'validation'
        );
      }
      
      console.log('[MailApp:SendMessage] Using integration:', integration.name);
      
      // Parse recipients
      const toList = parseEmailList(to);
      const ccList = cc ? parseEmailList(cc) : undefined;
      const bccList = bcc ? parseEmailList(bcc) : undefined;
      
      if (toList.length === 0) {
        return createErrorResult('Invalid recipients', 'No valid email addresses in "to" field', 'validation');
      }
      
      console.log('[MailApp:SendMessage] Recipients parsed', { 
        to: toList.length, 
        cc: ccList?.length, 
        bcc: bccList?.length 
      });
      
      // Two send paths:
      // 1. Gmail integration present → POST /api/mail/gmail/send (user's OAuth account)
      // 2. No integration / fallback → POST /api/mail/send (Mailgun, River's domain)
      const hasGmailIntegration = integration &&
        (integration.entityType === 'Integration' || integration.entityType?.startsWith('Integration.'));

      if (hasGmailIntegration) {
        // Gmail path — uses user's connected account
        console.log('[MailApp:SendMessage] Sending via Gmail integration:', integrationId);
        const result = await fetchService(`${API_BASE_URL}/mail/gmail/send`, {
          method: 'POST',
          body: JSON.stringify({
            integration_id: integrationId,
            to: toList,
            subject,
            body_plain: body,
            cc: ccList,
            bcc: bccList,
            in_reply_to: inReplyTo,
            thread_id: threadId,
          }),
        });
        console.log('[MailApp:SendMessage] ✅ Sent via Gmail:', result.messageId);
        return createSuccessResult(
          `Email sent to ${toList.map((p: any) => p.email).join(', ')}`,
          { messageId: result.messageId, threadId: result.threadId, to: toList.map((p: any) => p.email), subject }
        );
      } else {
        // Mailgun fallback — sends from River's domain
        console.log('[MailApp:SendMessage] No Gmail integration — falling back to Mailgun');
        const toEmails = toList.map((p: any) => p.name ? `${p.name} <${p.email}>` : p.email).join(', ');
        const result = await fetchService(`${API_BASE_URL}/mail/send`, {
          method: 'POST',
          body: JSON.stringify({ to: toEmails, subject, body }),
        });
        console.log('[MailApp:SendMessage] ✅ Sent via Mailgun:', result.messageId);
        return createSuccessResult(
          `Email sent to ${toEmails} (via River)`,
          { messageId: result.messageId, to: toList.map((p: any) => p.email), subject, via: 'mailgun' }
        );
      }
    } catch (error) {
      console.error('[MailApp:SendMessage] ❌ Error:', error);
      return createErrorResult(
        'Failed to send email',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};
